import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface FacilityContact {
  contact_id: string
  facility_id: string
  tenant_id: string
  contact_name: string
  email: string
  phone: string | null
  role_label: string | null
  is_primary: boolean
  created_by: string
  created_at: string
  updated_at: string
}

export interface ReportDelivery {
  delivery_id: string
  report_id: string
  contact_name: string
  contact_email: string
  facility_name: string
  pdf_sha256: string
  status: 'queued' | 'sent' | 'delivered' | 'opened' | 'bounced' | 'failed'
  sent_at: string
  delivered_at: string | null
  opened_at: string | null
  bounce_reason: string | null
}

export interface CreateFacilityContactInput {
  facility_id: string
  contact_name: string
  email: string
  phone?: string
  role_label?: string
  is_primary?: boolean
}

export interface SendReportInput {
  report_id: string
  contact_ids: string[]
  cc_emails?: string[]
  message?: string
}

export async function listFacilityContacts(facilityId: string): Promise<FacilityContact[]> {
  const { data, error } = await invokeFunction<{ contacts: FacilityContact[] }>('facility-contacts', {
    method: 'GET',
    path: `?facility_id=${encodeURIComponent(facilityId)}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load facility contacts')
  return Array.isArray(data?.contacts) ? data.contacts : []
}

export async function createFacilityContact(input: CreateFacilityContactInput): Promise<FacilityContact> {
  const { data, error } = await invokeFunction<FacilityContact>('facility-contacts', {
    method: 'POST',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to create contact')
  if (!data) throw new Error('No data returned')
  return data
}

export async function updateFacilityContact(
  contactId: string,
  updates: Partial<Omit<CreateFacilityContactInput, 'facility_id'>>,
): Promise<FacilityContact> {
  const { data, error } = await invokeFunction<FacilityContact>('facility-contacts', {
    method: 'PATCH',
    path: `/${contactId}`,
    body: updates,
  })
  if (error) throw toApiRequestError(error, 'Failed to update contact')
  if (!data) throw new Error('No data returned')
  return data
}

export async function deleteFacilityContact(contactId: string): Promise<void> {
  const { error } = await invokeFunction('facility-contacts', {
    method: 'DELETE',
    path: `/${contactId}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to delete contact')
}

export async function listReportDeliveries(reportId: string): Promise<ReportDelivery[]> {
  const { data, error } = await invokeFunction<{ deliveries: ReportDelivery[] }>('reports-send', {
    method: 'GET',
    path: `?report_id=${encodeURIComponent(reportId)}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load delivery log')
  return Array.isArray(data?.deliveries) ? data.deliveries : []
}

export async function sendReport(input: SendReportInput): Promise<{ delivery_ids: string[] }> {
  const { data, error } = await invokeFunction<{ delivery_ids: string[]; status: string }>('reports-send', {
    method: 'POST',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to send report')
  if (!data) throw new Error('No data returned')
  return { delivery_ids: data.delivery_ids }
}

export async function inviteFieldSurveyor(input: {
  email: string
  facility_ids: string[]
  first_name?: string
  last_name?: string
}): Promise<{ user_id: string; email: string; facility_assignment_count: number }> {
  const { data, error } = await invokeFunction<{ user: { user_id: string; email: string }; facility_assignment_count: number }>('admin-users', {
    method: 'POST',
    path: '/invite-field-surveyor',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to invite field surveyor')
  if (!data) throw new Error('No data returned')
  return { user_id: data.user.user_id, email: data.user.email, facility_assignment_count: data.facility_assignment_count }
}
